import styles from './PunchupTicketDetails.module.css';

import { CreateTicketForm, ReqFieldsProps, TicketDetailsParams } from './types';
import {
	NEW_TICKET,
	createBody,
	defaultValues,
	fillForm,
	getTicketTypeCanGoLive,
	resolveTicketImage,
	showReqFieldsModal,
	validationSchema,
} from './utils';
import { useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Form } from 'features/common/components/Form';
import { TicketTypeDetailsForm } from './components/TicketTypeDetailsForm';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { PunchupPageHeader } from 'features/common/components/Punchup/primitives/layout/PunchupPageHeader';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { NoPoolConfirmDialog } from './components/NoPoolConfirmDialog';
import { RequiredFieldsModal } from 'features/Embed/components/RequiredFields/RequieredFieldsModal';
import { useForm } from 'react-hook-form';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTicketTypeApi } from './useTicketTypeApi';
import { useSeededEditForm } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { shouldWarnNoPool } from 'features/common/utils/poolGuards';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

const PunchupTicketDetails: React.FC = () => {
	const { goBack } = useHistory();
	const { t } = useTranslation();
	const { id: eventId, ticketId } = useParams<TicketDetailsParams>();
	const location = useLocation();
	const { event, isError: isEventError } = useGetEvent();
	const {
		ticket,
		isTicketError,
		canGoLive,
		pools,
		mutateUploadImage,
		mutateUpdateTicket,
		mutateCreateTicket,
		mutateDeleteTicket,
	} = useTicketTypeApi();
	const [showReqFields, setShowReqFields] = useState(false);
	const [reqFieldsProps, setReqFieldsProps] = useState<ReqFieldsProps>();
	const [showNoPoolConfirm, setShowNoPoolConfirm] = useState(false);

	const isNewTicket = location.pathname.includes(NEW_TICKET);

	// Quantity floor (already-sold), read at validation time so it reflects the loaded ticket.
	const soldFloorRef = useRef(0);
	soldFloorRef.current = ticket?.sold ?? 0;

	const formMethods = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema(t, () => soldFloorRef.current)),
	});

	// Seed the edit form once when both the ticket and event have loaded, held behind a spinner until then so
	// toggles/date-pickers don't paint defaults and re-select (the blink). A later refetch (window refocus, a
	// shared-key invalidation) must NOT re-reset and clobber in-progress edits — useSeededEditForm guards that.
	// Create mode renders immediately. Mirrors the accordion TicketTypeForm seed.
	const { isSeeded, seedGate } = useSeededEditForm({
		isEditMode: !isNewTicket,
		formMethods,
		seedData: event && ticket ? { ticket, event } : undefined,
		toFormValues: ({ ticket, event }) => fillForm(ticket, event.timeZone) ?? defaultValues,
		isError: isTicketError || isEventError,
	});

	// The actual save — reached directly, or after the "no pool" warning is confirmed. The create/update
	// mutations surface their own failures via onError (showNotification); the image upload is toasted by
	// mutateUploadImage.onError. So the only thing that can still throw here is resolveTicketImage —
	// preserve its cause for the boundary rather than stringifying it into a contentless message.
	const proceedSubmit = async () => {
		try {
			const values = formMethods.getValues();
			const { image, displayType } = await resolveTicketImage(values, mutateUploadImage, Number(eventId));
			const newTicketBody = createBody(
				{ ...values, image, displayType },
				Number(eventId),
				event?.timeZone || '',
				ticket?.ID
			);
			if (isNewTicket) {
				mutateCreateTicket(newTicketBody);
			} else {
				mutateUpdateTicket(newTicketBody);
			}
		} catch (error) {
			throw error instanceof Error ? error : new Error(String(error));
		}
	};

	const onSubmitTicketForm = async (currentValues: CreateTicketForm) => {
		if (event && canGoLive && showReqFieldsModal(event, canGoLive, currentValues.initialPrice || 0)) {
			setReqFieldsProps({
				eventGoLive: getTicketTypeCanGoLive(canGoLive, ticket?.ID),
				eventId: Number(eventId),
				type: 'ticket',
				closeModal: () => setShowReqFields(false),
			});

			setShowReqFields(true);
			return;
		}

		// Warn once if a brand-new tier joins no pool while the show has pools (T-5).
		if (shouldWarnNoPool(isNewTicket, pools?.length ?? 0, currentValues.poolId)) {
			setShowNoPoolConfirm(true);
			return;
		}

		await proceedSubmit();
	};

	// A failed ticket/event load would otherwise paint a blank default-valued form (the seed never runs).
	// Surface the failure with a retry instead, matching sibling PunchupPages surfaces.
	if (!isNewTicket && (isTicketError || isEventError)) {
		return (
			<PunchupPageShell pageName="Edit Ticket Type" onBack={goBack}>
				<PunchupErrorState
					title={t('ticketDetails-load-error-title', "We couldn't load this ticket type")}
					description={t('ticketDetails-load-error-description', 'Please try again.')}
					onRetry={() => window.location.reload()}
				/>
			</PunchupPageShell>
		);
	}

	// Edit mode is held behind the spinner until the saved ticket is seeded (create renders immediately).
	if (!isSeeded) return <>{seedGate}</>;

	return (
		<PunchupPageShell pageName={isNewTicket ? 'Create Ticket Type' : 'Edit Ticket Type'} onBack={goBack}>
			<Form className={styles.styledForm} formMethods={formMethods} onSubmit={onSubmitTicketForm}>
				<PunchupPageHeader
					title={isNewTicket ? 'Create Ticket Type' : 'Edit Ticket Type'}
					description={
						isNewTicket ? 'Add a new ticket type to your show.' : 'Edit the details and settings for this ticket type.'
					}
				/>

				<div className={styles.contentWrapper}>
					<TicketTypeDetailsForm
						isNewTicket={isNewTicket}
						onDelete={!isNewTicket ? () => mutateDeleteTicket(Number(ticketId)) : undefined}
					/>
				</div>

				<div className={styles.punchupButtonContainer}>
					<PunchupButton type="button" variant="secondary" onClick={goBack} text="Cancel" />
					<PunchupButton type="submit" variant="primary" text={isNewTicket ? 'Create' : 'Update'} />
				</div>

				{showReqFields && reqFieldsProps && <RequiredFieldsModal {...reqFieldsProps} />}

				{showNoPoolConfirm && (
					<NoPoolConfirmDialog
						onBack={() => setShowNoPoolConfirm(false)}
						onContinue={() => {
							setShowNoPoolConfirm(false);
							proceedSubmit();
						}}
					/>
				)}
			</Form>
		</PunchupPageShell>
	);
};

export { PunchupTicketDetails };
